import { useMemo, useState } from 'react';
import { Button, Card, LoadingSkeleton } from '@nexora/design-system';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import {
  useReconciliation,
  type MatchConfidence,
  type ReconciliationTransaction,
} from './useReconciliation';

/* ── Situação da linha do extrato ─────────────────────────────────────────
   A referência tem quatro estados: Conciliado, Sugestão, Revisar e Pendente.
   Eles saem direto da confiança que o motor de casamento do domínio grava:
   HIGH é casamento exato (valor e data, ou número de documento), MEDIUM exige
   descrição parecida dentro da tolerância, LOW é só proximidade de valor. */

type LineState = 'matched' | 'suggested' | 'review' | 'pending';

const STATE_BY_CONFIDENCE: Record<MatchConfidence, LineState> = {
  HIGH: 'matched',
  MEDIUM: 'suggested',
  LOW: 'review',
};

const STATE_LABEL: Record<LineState, string> = {
  matched: 'Conciliado',
  suggested: 'Sugestão',
  review: 'Revisar',
  pending: 'Pendente',
};

const STATE_ICON: Record<LineState, string> = {
  matched: 'check_circle',
  suggested: 'lightbulb',
  review: 'search',
  pending: 'pending',
};

function lineState(tx: ReconciliationTransaction): LineState {
  if (!tx.matchedTransactionId) return 'pending';
  return tx.matchConfidence ? STATE_BY_CONFIDENCE[tx.matchConfidence] : 'suggested';
}

const FILTERS: { key: LineState | 'all'; label: string }[] = [
  { key: 'all', label: 'Todas' },
  { key: 'matched', label: 'Conciliado' },
  { key: 'suggested', label: 'Sugestão' },
  { key: 'review', label: 'Revisar' },
  { key: 'pending', label: 'Pendente' },
];

/* ── Workspace ── */

export function ReconciliationWorkspace({
  reconciliationId,
  onBack,
  onConfirm,
  confirming,
}: {
  reconciliationId: string;
  onBack: () => void;
  onConfirm: (id: string) => void;
  confirming: boolean;
}): JSX.Element {
  const { data, isLoading, error } = useReconciliation(reconciliationId);
  const [filter, setFilter] = useState<LineState | 'all'>('all');

  const lines = useMemo(() => data?.transactions ?? [], [data]);
  const visible = useMemo(
    () => (filter === 'all' ? lines : lines.filter((tx) => lineState(tx) === filter)),
    [lines, filter],
  );

  /* Lançamentos do ERP que não têm par no extrato. Não aparecem na grade
     lado a lado porque não há linha de extrato a que encostá-los — mas
     também não podem sumir: é aí que mora a diferença de saldo. */
  const systemOnly = useMemo(
    () => (data?.unmatchedEntries ?? []).filter((e) => e.type === 'SYSTEM_ONLY'),
    [data],
  );

  if (isLoading) {
    return (
      <Card padding="1rem">
        <LoadingSkeleton count={6} height="44px" />
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card padding="2rem" className="rw-state rw-state--error">
        <p className="rw-state__title">Erro ao carregar a conciliação</p>
        <p className="rw-state__detail">
          {error instanceof Error ? error.message : 'Conciliação não encontrada'}
        </p>
        <Button size="sm" variant="outline" onClick={onBack}>Voltar</Button>
      </Card>
    );
  }

  const progress = data.importedCount > 0
    ? Math.round((data.matchedCount / data.importedCount) * 100)
    : 0;
  const reconciled = data.difference === 0;

  return (
    <div className="rw">
      {/* ── Barra de contexto ── */}
      <div className="rw-bar">
        <Button size="sm" variant="ghost" onClick={onBack}>← Conciliações</Button>
        <div className="rw-bar__account">
          <span className="rw-bar__label">Conta bancária</span>
          <span className="rw-bar__value">{data.accountName}</span>
        </div>
        <div className="rw-bar__account">
          <span className="rw-bar__label">Período</span>
          <span className="rw-bar__value rw-mono">
            {formatDate(data.periodStart)} — {formatDate(data.periodEnd)}
          </span>
        </div>
        <div className="rw-bar__spacer" />
        {data.status === 'DRAFT' && (
          <Button size="sm" onClick={() => onConfirm(data.id)} loading={confirming}>
            Confirmar conciliação
          </Button>
        )}
      </div>

      {/* ── Indicadores ── */}
      <div className="rw-kpis">
        <Card variant="outlined" padding="1rem">
          <div className="rw-kpi__head">
            <span className="rw-kpi__label">Saldo Banco (extrato)</span>
            <span className="material-symbols-outlined rw-kpi__icon" aria-hidden="true">account_balance</span>
          </div>
          <div className="rw-kpi__value">{formatCurrency(data.closingBalance)}</div>
          <div className="rw-kpi__foot">Extrato de {formatDate(data.statementDate)}</div>
        </Card>

        <Card variant="outlined" padding="1rem">
          <div className="rw-kpi__head">
            <span className="rw-kpi__label">Saldo ERP</span>
            <span className="material-symbols-outlined rw-kpi__icon" aria-hidden="true">storage</span>
          </div>
          <div className="rw-kpi__value">{formatCurrency(data.systemBalance)}</div>
          <div className="rw-kpi__foot">Saldo da conta no sistema</div>
        </Card>

        <Card variant="outlined" padding="1rem" className={reconciled ? '' : 'rw-kpi--alert'}>
          <div className="rw-kpi__head">
            <span className="rw-kpi__label">Diferença</span>
            <span className="material-symbols-outlined rw-kpi__icon" aria-hidden="true">
              {reconciled ? 'check_circle' : 'warning'}
            </span>
          </div>
          <div className={reconciled ? 'rw-kpi__value rw-kpi__value--ok' : 'rw-kpi__value rw-kpi__value--bad'}>
            {formatCurrency(data.difference)}
          </div>
          <div className="rw-kpi__foot">
            {reconciled ? 'Extrato e sistema batem' : 'Requer ação'}
          </div>
        </Card>

        <Card variant="outlined" padding="1rem">
          <div className="rw-kpi__head">
            <span className="rw-kpi__label">Progresso</span>
            <span className="material-symbols-outlined rw-kpi__icon" aria-hidden="true">donut_large</span>
          </div>
          <div className="rw-kpi__value">
            {progress}%<span className="rw-kpi__unit"> conciliado</span>
          </div>
          <div className="rw-progress" role="img" aria-label={`${progress} por cento conciliado`}>
            <div className="rw-progress__fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="rw-kpi__foot rw-mono">
            {data.matchedCount} de {data.importedCount} linhas
          </div>
        </Card>
      </div>

      {/* ── Grade de conciliação ── */}
      <Card variant="outlined" padding="0">
        <div className="rw-grid__head">
          <div className="rw-grid__side">
            <span className="material-symbols-outlined rw-grid__icon" aria-hidden="true">download</span>
            <span className="rw-grid__title">Extrato bancário</span>
            <span className="rw-grid__count rw-mono">{lines.length} linhas</span>
          </div>
          <div className="rw-grid__side">
            <span className="material-symbols-outlined rw-grid__icon" aria-hidden="true">storage</span>
            <span className="rw-grid__title">Lançamento no ERP</span>
          </div>
        </div>

        <div className="rw-filters" role="group" aria-label="Filtrar linhas do extrato">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={filter === f.key ? 'rw-filter is-active' : 'rw-filter'}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              <span className="rw-filter__count">
                {f.key === 'all' ? lines.length : lines.filter((tx) => lineState(tx) === f.key).length}
              </span>
            </button>
          ))}
        </div>

        {visible.length === 0 ? (
          <p className="rw-empty">
            {lines.length === 0
              ? 'Esta conciliação não tem linhas de extrato importadas.'
              : 'Nenhuma linha nesta situação.'}
          </p>
        ) : (
          <ul className="rw-rows">
            {visible.map((tx) => {
              const state = lineState(tx);
              const divergente = (tx.matchDifference ?? 0) > 0;
              return (
                <li key={tx.id} className={`rw-row rw-row--${state}`}>
                  {/* lado do extrato */}
                  <div className="rw-row__bank">
                    <span className="rw-row__date rw-mono">{formatDate(tx.date)}</span>
                    <span className="rw-row__desc">{tx.description}</span>
                    <span className={tx.amount < 0 ? 'rw-row__amount rw-row__amount--out' : 'rw-row__amount'}>
                      {formatCurrency(tx.amount)}
                    </span>
                    <span className={`rw-tag rw-tag--${state}`}>
                      <span className="material-symbols-outlined rw-tag__icon" aria-hidden="true">
                        {STATE_ICON[state]}
                      </span>
                      {STATE_LABEL[state]}
                    </span>
                  </div>

                  {/* lado do ERP */}
                  <div className="rw-row__erp">
                    {tx.matchedTransactionId ? (
                      <>
                        <div className="rw-row__match">
                          <span className="rw-row__desc">{tx.matchedDescription ?? tx.matchedTransactionId}</span>
                          <span className="rw-row__hint">
                            {divergente
                              ? `Valor difere do extrato em ${formatCurrency(tx.matchDifference ?? 0)}`
                              : `Casamento ${tx.matchConfidence === 'HIGH' ? 'exato' : 'por aproximação'}`}
                            {tx.matchedDate ? ` · ${formatDate(tx.matchedDate)}` : ''}
                          </span>
                        </div>
                        <span
                          className={
                            (tx.matchedAmount ?? 0) < 0
                              ? 'rw-row__amount rw-row__amount--out'
                              : 'rw-row__amount'
                          }
                        >
                          {formatCurrency(tx.matchedAmount ?? 0)}
                        </span>
                      </>
                    ) : (
                      <div className="rw-row__match">
                        <span className="rw-row__none">
                          {tx.unmatchedReason ?? 'Nenhum lançamento correspondente encontrado.'}
                        </span>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {systemOnly.length > 0 && (
        <Card variant="outlined" padding="0" className="rw-systemonly">
          <div className="rw-grid__head rw-grid__head--single">
            <div className="rw-grid__side">
              <span className="material-symbols-outlined rw-grid__icon" aria-hidden="true">report</span>
              <span className="rw-grid__title">Lançamentos do ERP sem par no extrato</span>
              <span className="rw-grid__count rw-mono">{systemOnly.length}</span>
            </div>
          </div>
          <ul className="rw-rows">
            {systemOnly.map((entry) => (
              <li key={entry.id} className="rw-row rw-row--pending rw-row--single">
                <div className="rw-row__bank">
                  <span className="rw-row__date rw-mono">
                    {entry.date ? formatDate(entry.date) : '—'}
                  </span>
                  <span className="rw-row__desc">{entry.description ?? entry.transactionId}</span>
                  <span className={(entry.amount ?? 0) < 0 ? 'rw-row__amount rw-row__amount--out' : 'rw-row__amount'}>
                    {formatCurrency(entry.amount ?? 0)}
                  </span>
                  <span className="rw-row__hint">{entry.reason}</span>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {data.notes && (
        <Card variant="outlined" padding="0.75rem 1rem" className="rw-notes">
          <strong>Observações:</strong> {data.notes}
        </Card>
      )}

      {data.status === 'DRAFT' && (
        <p className="rw-foot">
          {/* A referência oferece Confirmar / Vincular / Ajustar por linha e um
              "Auto-Conciliar (N)". Nenhum deles existe como endpoint: o backend
              só expõe a confirmação da conciliação inteira, e o motor de
              sugestão roda na criação, não sob demanda. Botão por linha aqui
              seria botão que não faz nada. */}
          O casamento é calculado na importação do extrato. A confirmação vale
          para a conciliação inteira e ajusta o saldo da conta.
        </p>
      )}

      <style>{`
        .rw { display: flex; flex-direction: column; gap: 1rem; }
        .rw-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; }

        .rw-bar { display: flex; align-items: center; gap: 1.5rem; flex-wrap: wrap; padding: 0.75rem 1rem; background: #ffffff; border: 1px solid #c4c6cf; border-radius: 4px; }
        .rw-bar__spacer { flex: 1; }
        .rw-bar__label { display: block; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase; color: #43474e; }
        .rw-bar__value { display: block; font-size: 0.875rem; font-weight: 600; color: #191c1e; }

        .rw-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1rem; }
        .rw-kpi__head { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
        .rw-kpi__label { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.04em; text-transform: uppercase; color: #43474e; }
        .rw-kpi__icon { font-size: 18px; color: #74777f; }
        .rw-kpi__value { margin-top: 0.375rem; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; font-size: 1.375rem; font-weight: 600; color: #191c1e; }
        .rw-kpi__value--ok { color: #1b7f4d; }
        .rw-kpi__value--bad { color: #ba1a1a; }
        .rw-kpi__unit { font-size: 0.8125rem; font-weight: 400; color: #43474e; }
        .rw-kpi__foot { margin-top: 0.25rem; font-size: 0.75rem; color: #74777f; }
        .rw-kpi--alert { border-color: #ba1a1a; }
        .rw-progress { margin-top: 0.5rem; height: 6px; background: #e0e3e5; border-radius: 2px; overflow: hidden; }
        .rw-progress__fill { height: 100%; background: #002045; }

        .rw-grid__head { display: grid; grid-template-columns: 1fr 1fr; border-bottom: 1px solid #c4c6cf; }
        .rw-grid__head--single { grid-template-columns: 1fr; }
        .rw-grid__side { display: flex; align-items: center; gap: 0.5rem; padding: 0.625rem 1rem; }
        .rw-grid__side + .rw-grid__side { border-left: 1px solid #c4c6cf; }
        .rw-grid__icon { font-size: 18px; color: #43474e; }
        .rw-grid__title { font-size: 0.875rem; font-weight: 600; color: #191c1e; }
        .rw-grid__count { margin-left: auto; font-size: 11px; color: #43474e; }

        .rw-filters { display: flex; gap: 0.375rem; flex-wrap: wrap; padding: 0.625rem 1rem; border-bottom: 1px solid #eceef0; background: #f7f9fb; }
        .rw-filter { display: inline-flex; align-items: center; gap: 0.375rem; padding: 0.25rem 0.625rem; background: #ffffff; border: 1px solid #c4c6cf; border-radius: 2px; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: #43474e; cursor: pointer; }
        .rw-filter.is-active { background: #002045; border-color: #002045; color: #ffffff; }
        .rw-filter__count { font-variant-numeric: tabular-nums; opacity: 0.75; }

        .rw-rows { list-style: none; margin: 0; padding: 0; }
        .rw-row { display: grid; grid-template-columns: 1fr 1fr; border-bottom: 1px solid #eceef0; }
        .rw-row--single { grid-template-columns: 1fr; }
        .rw-row:hover { background: #f7f9fb; }
        .rw-row__bank { display: grid; grid-template-columns: 78px 1fr 130px auto; align-items: center; gap: 0.75rem; padding: 0.625rem 1rem; }
        .rw-row__erp { display: grid; grid-template-columns: 1fr 130px; align-items: center; gap: 0.75rem; padding: 0.625rem 1rem; border-left: 1px solid #eceef0; }
        .rw-row--matched .rw-row__erp { border-left: 3px solid #1b7f4d; }
        .rw-row--suggested .rw-row__erp { border-left: 3px solid #b8860b; }
        .rw-row--review .rw-row__erp { border-left: 3px solid #c25a02; }
        .rw-row--pending .rw-row__erp { border-left: 3px solid #c4c6cf; }
        .rw-row__date { font-size: 12px; color: #43474e; white-space: nowrap; }
        .rw-row__desc { font-size: 0.875rem; color: #191c1e; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .rw-row__hint { display: block; font-size: 0.75rem; color: #74777f; }
        .rw-row__none { font-size: 0.8125rem; color: #74777f; font-style: italic; }
        .rw-row__amount { text-align: right; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; font-size: 0.875rem; white-space: nowrap; }
        .rw-row__amount--out { color: #ba1a1a; }

        .rw-tag { display: inline-flex; align-items: center; gap: 0.25rem; padding: 1px 6px; border-radius: 2px; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: 0.02em; white-space: nowrap; }
        .rw-tag__icon { font-size: 13px; }
        .rw-tag--matched { background: #d3f5d6; color: #1b5e20; }
        .rw-tag--suggested { background: #fff3cd; color: #7a5b00; }
        .rw-tag--review { background: #ffe0c2; color: #7a3f00; }
        .rw-tag--pending { background: #e0e3e5; color: #43474e; }

        .rw-empty { margin: 0; padding: 2.5rem 1rem; text-align: center; color: #74777f; font-size: 0.875rem; }
        .rw-notes { font-size: 0.875rem; color: #43474e; }
        .rw-foot { margin: 0; font-size: 0.8125rem; color: #74777f; }

        .rw-state { text-align: center; }
        .rw-state__title { margin: 0; font-weight: 600; color: #ba1a1a; }
        .rw-state__detail { margin: 0.5rem 0 1rem; font-size: 0.875rem; color: #74777f; }
      `}</style>
    </div>
  );
}
